import React, { useEffect, useState } from "react";
import {
  Button,
  Popover,
  createTheme,
  ThemeProvider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Grid,
  FormControlLabel,
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  OutlinedInput,
} from "@mui/material";
import {
  Dialog as DialogX,
  DialogContent as DialogContentX,
  DialogHeader,
  DialogTitle as DialogTitleX,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Loader2 } from "lucide-react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LockLaminated, LockLaminatedOpen } from "@phosphor-icons/react";
import { green } from "@mui/material/colors";
import { apiClient } from "../adapters/api";
import { GetAllCourses, useVacancyDetails } from "../adapters/Requests";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { getVacancies } from "../adapters/Requests";
import { internshipTypes } from "../utils/constants";

const theme = createTheme({
  palette: {
    primary: {
      main: green[600],
      dark: green[700],
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#f8fafc',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: green[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: green[600],
            borderWidth: 2,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: green[700],
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const { IT, GAP, SIWES } = internshipTypes;

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

const CourseSelection = ({
  selectedCourses,
  setSelectedCourses,
  intershipType,
  siwesQuotas,
  handleSiwesQuotasChange,
  handleSiwesQuotaRemoval,
}) => {
  const { data: AllCourses, isLoading: coursesLoading } = GetAllCourses();

  useEffect(() => {
    if (AllCourses) {
      const selected = AllCourses.map((course) => course.id);
      setSelectedCourses(selected);
    }
  }, [AllCourses, setSelectedCourses]);

  // const [courses, setCourses] = useState(COURSE_LIST);
  // const [newCourse, setNewCourse] = useState("");

  // const handleAddCourse = () => {
  //   if (newCourse && !courses.includes(newCourse)) {
  //     setCourses([...courses, newCourse]);
  //     setSelectedCourses([...selectedCourses, newCourse]);
  //     setNewCourse("");
  //   }
  // };

  const handleCourseChange = (event) => {
    const value = Number(event.target.value);
    if (selectedCourses.includes(value)) {
      setSelectedCourses(selectedCourses.filter((course) => course !== value));
      handleSiwesQuotaRemoval(value);
    } else {
      setSelectedCourses([...selectedCourses, value]);
    }
  };

  return (
    <div className="mt-6 mb-10 m-auto">
      <h3 className="font-thin text-md mb-4">Select Courses</h3>
      <Grid container spacing={2}>
        {!coursesLoading &&
          AllCourses.map((course) => {
            const selected = selectedCourses.includes(course.id);
            return (
              <Grid
                item
                xs={4}
                key={course.id}
                className="flex justify-between"
              >
                <FormControlLabel
                  // className="w-40"
                  control={
                    <Checkbox
                      checked={selected}
                      onChange={handleCourseChange}
                      value={course.id}
                      sx={{
                        color: green[600],
                        "&.Mui-checked": {
                          color: green[600],
                        },
                      }}
                    />
                  }
                  label={course.name}
                />
                {intershipType === SIWES && selected && (
                  <input
                    type="text"
                    className="w-10 px-0 py-1 text-center self-center outline-none border-0 border-b-2 bg-gray-100"
                    value={siwesQuotas[course.id] || ""}
                    onChange={(event) =>
                      handleSiwesQuotasChange(event, course.id)
                    }
                  />
                )}
              </Grid>
            );
          })}
      </Grid>
      {/* <div className="flex justify-end mr-10  mb-6 w-full">
        <TextField
          value={newCourse}
          onChange={(e) => setNewCourse(e.target.value)}
          label="Add course"
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: "green",
              },
              "&.Mui-focused fieldset": {
                borderColor: "green",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "green",
            },
          }}
        />
        <Button
          onClick={handleAddCourse}
          style={{ marginLeft: "10px" }}
          variant="contained"
          color="success"
        >
          Add
        </Button>
      </div> */}
    </div>
  );
};

const CustomDatePicker = ({ label, value, onChange, minDate }) => (
  <DatePicker
    label={label}
    format="DD/MM/YYYY"
    value={value}
    onChange={onChange}
    renderInput={(params) => <TextField {...params} fullWidth />}
    minDate={minDate}
    sx={{
      "& .MuiOutlinedInput-root": {
        "&:hover fieldset": {
          borderColor: "green",
        },
        "&.Mui-focused fieldset": {
          borderColor: "green",
        },
        "&.Mui-focused": {
          outline: "none",
          boxShadow: "none",
        },
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "green",
      },
      "& .MuiInputBase-input:focus": {
        boxShadow: "none",
      },
    }}
  />
);

export default function ManageJobVacancies() {
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "",
  });
  const { data: existingVacancies } = getVacancies();
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedValue, setSelectedValue] = useState("it");
  const [status, setStatus] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [location, setLocation] = useState("");
  const [quota, setQuota] = useState("");
  const [siwesQuotas, setSiwesQuotas] = useState({});
  const [batch, setBatch] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]); // Initialize with all courses
  const [open, setOpen] = React.useState(false);
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [startYear, setStartYear] = useState("");
  const [endYear, setEndYear] = useState("");
  const [availableStartMonths, setAvailableStartMonths] = useState([]);
  const navigate = useNavigate();
  const apiClientPrivate = useAxiosPrivate();
  const [newVancancy, setNewVancancy] = useState({});
  const [loading, setLoading] = useState(false);

  const handleClickOpen = () => {
    const vacancyData = {
      vacancy_position: selectedValue,
      business_unit_id: businessUnit,
      location: location,
      batch: batch,
      opening_date: startDate,
      closing_date: endDate?.add(1, "day").subtract(1, "second"),
      status: status,
      courses: selectedCourses,
    };

    if (selectedValue == SIWES) {
      if (!selectedCourses || selectedCourses.length === 0) {
        toast.error("Please select at least one course for SIWES.");
        return;
      }

      for (let i = 0; i < selectedCourses.length; i++) {
        if (!siwesQuotas[selectedCourses[i]]) {
          toast.error("Please add quotas to all selected courses for SIWES vacancy.");
          return;
        }
      }

      const total = Object.values(siwesQuotas).reduce(
        (sum, value) => sum + Number(value),
        0
      );

      vacancyData.siwesQuotas = siwesQuotas;
      vacancyData.quota = total;
    } else {
      vacancyData.quota = quota;
    }

    // Validation check for missing fields
    if (
      !vacancyData.vacancy_position ||
      !vacancyData.business_unit_id ||
      !vacancyData.location ||
      !vacancyData.quota ||
      !vacancyData.batch ||
      !vacancyData.opening_date ||
      !vacancyData.closing_date ||
      !vacancyData.status ||
      !vacancyData.courses ||
      vacancyData.courses.length === 0
    ) {
      toast.error("Fill in all fields to add vacancy");
      return; // Exit the function if validation fails
    }

    setNewVancancy(vacancyData);

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { data: vacancyDetails, isLoading, isError } = useVacancyDetails();

  const handleChangeStatus = (event) => {
    setStatus(event.target.value);
  };

  const renderStatusValue = (value) => {
    switch (value) {
      case "open":
        return (
          <span>
            <LockLaminatedOpen
              size={18}
              color="green"
              className="inline-block mr-1"
            />
            - Open
          </span>
        );
      case "closed":
        return (
          <span>
            <LockLaminated
              color="red"
              size={18}
              className="inline-block mr-1"
            />
            - Closed
          </span>
        );
      default:
        return <em>None</em>;
    }
  };

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const handleSave = async () => {
    try {
      // Basic Validation
      if (!newVancancy.vacancy_position || !newVancancy.business_unit_id || !newVancancy.location || !newVancancy.quota || !newVancancy.batch || !newVancancy.opening_date || !newVancancy.closing_date) {
        toast.error("Please fill in all required fields.");
        return;
      }

      // SIWES Quota Validation
      if (newVancancy.vacancy_position === internshipTypes.SIWES) {
        if (!selectedCourses || selectedCourses.length === 0) {
          toast.error("Please select at least one course for SIWES.");
          return;
        }

        for (const courseId of selectedCourses) {
          const quotaStr = siwesQuotas[courseId];
          const quotaNum = Number(quotaStr);
          if (!quotaStr || isNaN(quotaNum) || quotaNum <= 0) {
            toast.error(
              "Please provide a valid quota for all selected SIWES courses."
            );
            return;
          }
        }
      }

      const submissionData = {
        ...newVancancy,
        courses: selectedCourses,
        siwesQuotas,
      };

      setLoading(true);
      const similarVacancy = existingVacancies.find(
        (vacancy) =>
          vacancy.vacancy_position.name === newVancancy.vacancy_position &&
          vacancy.status === newVancancy.status &&
          vacancy.location.name === newVancancy.location &&
          vacancy.business_unit.id === newVancancy.business_unit_id &&
          vacancy.batch.name === newVancancy.batch
      );

      if (similarVacancy) {
        toast.error(
          "A similar vacancy already exists, you can close the first existing vacancy to create a new one"
        );
        return;
      }

      const response = await apiClientPrivate.post("/vacancy/add", submissionData);

      if (response.status === 201) {
        toast.success("Vacancy added successfully!");
        navigate("/vacancysettings");
      } else {
        toast.error("Error adding vacancy.");
      }
    } catch (error) {
      console.error("Error adding vacancy:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while adding the vacancy.";
      toast.error(errorMessage);
    } finally {
      handleClose();
      setLoading(false);
    }
  };

  const renderMenuItems = (monthsArray) => {
    return monthsArray.map((month) => (
      <MenuItem key={month} value={month}>
        {month}
      </MenuItem>
    ));
  };

  const handleStartMonthChange = (event) => {
    const newStartMonth = event.target.value;
    setStartMonth(newStartMonth);

    // Reset end month and year if they become invalid
    if (
      endYear === startYear &&
      months.indexOf(endMonth) < months.indexOf(newStartMonth)
    ) {
      setEndMonth("");
      setEndYear("");
    }
  };

  const handleEndMonthChange = (event) => {
    const newEndMonth = event.target.value;
    setEndMonth(newEndMonth);
    // Only update if the new end month is valid
    if (
      endYear > startYear ||
      (endYear === startYear &&
        months.indexOf(newEndMonth) >= months.indexOf(startMonth))
    ) {
      setEndMonth(newEndMonth);
    }
  };

  const handleStartYearChange = (event) => {
    const newStartYear = event.target.value;

    setStartYear(newStartYear);

    // Reset end year if it becomes invalid
    if (endYear < newStartYear) {
      setEndYear("");
    }
    // Reset end month if in the same year and it becomes invalid
    if (
      endYear === newStartYear &&
      months.indexOf(endMonth) < months.indexOf(startMonth)
    ) {
      setEndMonth("");
    }
  };

  const handleEndYearChange = (event) => {
    const newEndYear = event.target.value;
    // Only update if the new end year is valid
    if (
      newEndYear > startYear ||
      (newEndYear === startYear &&
        months.indexOf(endMonth) >= months.indexOf(startMonth))
    ) {
      setEndYear(newEndYear);
    } else {
      // If the year change makes the end month invalid, reset the end month
      setEndMonth("");
    }
  };

  useEffect(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const filteredMonths = months.filter((_, index) => {
      if (startYear && parseInt(startYear) > currentYear) {
        return true; // Show all months for future years
      }
      return index >= currentMonth; // Show only current and future months for the current year
    });

    setAvailableStartMonths(filteredMonths);
  }, [startYear]);

  useEffect(() => {
    if (startMonth && endMonth && startYear && endYear) {
      setBatch(`${startMonth} ${startYear} - ${endMonth} ${endYear}`);
    } else {
      setBatch("");
    }
  }, [startMonth, endMonth, startYear, endYear]);

  const getAvailableEndMonths = () => {
    if (startYear === endYear) {
      return months.slice(months.indexOf(startMonth));
    }
    return months;
  };

  const getAvailableEndYears = () => {
    return years.filter((year) => year >= startYear);
  };

  const handleSiwesQuotasChange = (event, key) => {
    const value = event.target.value;
    if (!(value === "" || (/^\d+$/.test(value) && Number(value) > 0))) return;

    setSiwesQuotas((prev) => ({ ...prev, [key]: value }));
  };

  const handleSiwesQuotaRemoval = (key) => {
    setSiwesQuotas((prev) => {
      delete prev[key];
      return prev;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/40">
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Create Vacancy</h1>
              <p className="text-sm text-gray-500 mt-1">Create new vacancy postings</p>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <form className="grid gap-6 w-full">
              {/* ── Vacancy Type ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Vacancy Type</p>
                <div className="grid grid-cols-3 gap-3">
                  {[IT, GAP, SIWES].map((option) => (
                    <label
                      key={option}
                      htmlFor={option}
                      className={`flex justify-between items-center px-5 py-4 rounded-xl border-2 cursor-pointer transition-all duration-200
              ${selectedValue === option
                          ? "bg-green-50 text-green-800 border-green-500 shadow-md shadow-green-100"
                          : "bg-slate-50 border-slate-200 hover:border-green-300 hover:bg-green-50/30"
                        }`}
                    >
                      <span className="font-semibold uppercase text-sm">{option}</span>
                      <input
                        type="radio"
                        id={option}
                        name="vacancyPosition"
                        className="h-5 w-5 text-green-500 accent-green-600"
                        checked={selectedValue === option}
                        onChange={() =>
                          handleChange({ target: { value: option } })
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>

              <hr className="border-slate-100" />

              {/* ── Application Window ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Application Window</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <CustomDatePicker
                      label="Opening Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      minDate={dayjs()}
                    />
                    <CustomDatePicker
                      label="Closing Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      minDate={startDate || dayjs()}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <hr className="border-slate-100" />

              {/* ── Vacancy Details ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Vacancy Details</p>
                <div className="grid gap-4">
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={status}
                      label="Status"
                      onChange={handleChangeStatus}
                    >
                      <MenuItem value="open">{renderStatusValue("open")}</MenuItem>
                      <MenuItem value="closed">
                        {renderStatusValue("closed")}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormControl fullWidth>
                      <InputLabel required id="business-unit-label">
                        Business Unit
                      </InputLabel>
                      <Select
                        labelId="business-unit-label"
                        id="business-unit"
                        value={businessUnit}
                        onChange={(e) => setBusinessUnit(e.target.value)}
                        label="Business Unit"
                        required
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <MenuItem disabled value="">Loading...</MenuItem>
                        ) : vacancyDetails?.businessUnits.map((unit) => (
                          <MenuItem key={unit.id} value={unit.id}>
                            {unit.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel required id="location-label">
                        Location
                      </InputLabel>
                      <Select
                        labelId="location-label"
                        id="location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        label="Location"
                        required
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <MenuItem disabled value="">Loading...</MenuItem>
                        ) : vacancyDetails?.locations.map((loc) => (
                          <MenuItem key={loc.id} value={loc.name}>
                            {loc.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {selectedValue !== internshipTypes.SIWES && (
                    <FormControl fullWidth>
                      <OutlinedInput
                        id="field"
                        type="number"
                        value={quota}
                        onChange={(e) => setQuota(e.target.value)}
                        placeholder="Quota (number of positions)"
                        required
                      />
                    </FormControl>
                  )}
                </div>
              </div>

              <hr className="border-slate-100" />

              {/* ── Internship Period ── */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Internship Period</p>
                {batch && (
                  <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-center">
                    <span className="text-sm font-medium text-green-800">{batch}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-slate-500">Start</p>
                    <div className="flex gap-2">
                      <FormControl fullWidth size="small">
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={startMonth}
                          label="Month"
                          onChange={handleStartMonthChange}
                        >
                          {availableStartMonths.map((month) => (
                            <MenuItem key={month} value={month}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl sx={{ minWidth: "110px" }} size="small">
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={startYear}
                          label="Year"
                          onChange={handleStartYearChange}
                        >
                          {years.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-slate-500">End</p>
                    <div className="flex gap-2">
                      <FormControl fullWidth size="small" disabled={!startMonth || !startYear}>
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={endMonth}
                          label="Month"
                          onChange={handleEndMonthChange}
                        >
                          {getAvailableEndMonths().map((month) => (
                            <MenuItem key={month} value={month}>
                              {month}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl
                        sx={{ minWidth: "110px" }}
                        size="small"
                        disabled={!startMonth || !startYear}
                      >
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={endYear}
                          label="Year"
                          onChange={handleEndYearChange}
                        >
                          {getAvailableEndYears().map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>

              <CourseSelection
                selectedCourses={selectedCourses}
                setSelectedCourses={setSelectedCourses}
                intershipType={selectedValue}
                siwesQuotas={siwesQuotas}
                handleSiwesQuotasChange={handleSiwesQuotasChange}
                handleSiwesQuotaRemoval={handleSiwesQuotaRemoval}
              />
            </form>
          </div>

          <div className="flex justify-center px-6 pb-8 pt-2">
            <Button
              variant="contained"
              color="success"
              onClick={handleClickOpen}
              className="mt-2 mb-2"
              size="large"
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                px: 6,
                py: 1.5,
                boxShadow: '0 4px 14px rgba(22, 163, 74, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(22, 163, 74, 0.4)',
                },
              }}
            >
              Save Vacancy
            </Button>
          </div>
        </div>
        {/* <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            You are about to create and save a new vacancy, Proceed?
          </DialogContent>
          <DialogActions>
            <Button className="" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={() => {
                handleSave();
                handleClose();
              }}
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog> */}
        {/* <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            You are about to create and save a new vacancy. Proceed?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              sx={{
                // text: 'red'
                color: "red",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "#ffcccc", // light red
                  color: "red",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              sx={{
                color: "white",
                backgroundColor: green[600],
                "&:hover": {
                  backgroundColor: green[700],
                },
              }}
            >
              {loading ? "Submitting..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog> */}
        <DialogX open={open} onOpenChange={setOpen}>
          <DialogContentX className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitleX className="text-xl font-semibold mb-4">
                Confirm Action
              </DialogTitleX>
              <DialogDescription className="text-base mt-2">
                Are you sure you want to create a new vacancy?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 space-x-2">
              <Button
                variant="outline"
                className="px-6 py-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="px-6 py-2 text-white bg-green-500 hover:bg-green-600 transition-colors"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {loading ? "Sending..." : "Confirm"}
              </Button>
            </DialogFooter>
          </DialogContentX>
        </DialogX>
      </div>
    </ThemeProvider>
  );
}
